/**
 * Le QCM de suivi façon QCMDashboard (11/10/2026), la partie sans écran.
 *
 * Le prof choisit un item dans le menu des tests ; la carte TanoEd de l'item tire ses variantes dans la page,
 * et rien n'est écrit en base avant le lancement. Au lancement, les variantes tirées et ajoutées sont
 * enregistrées une à une, comme des variantes de leur item (même Label : la comparaison avec septembre
 * marche sans rien changer), puis le QCM de suivi est créé avec les ids de tout ce qui a été choisi.
 */
import normalize from '../../../utils/normalize';
import { toPayloadUnifie, versVarianteEvalNat } from '../../../utils/evalNatAdapter';
import { avecSupportLecture, referenceSupportLecture } from '../../../utils/supportLecture';
import { MODULES_EVALNAT } from '../../../generators/evalnat';

const ORDRE_DES_TESTS = MODULES_EVALNAT.map((m) => m.typeLabel);
const rangDuTest = (typeLabel) => {
    const i = ORDRE_DES_TESTS.indexOf(typeLabel);
    return i === -1 ? ORDRE_DES_TESTS.length : i;
};
// Tri naturel des items : Q2 avant Q10
const numero = (questionNum) => parseInt(String(questionNum || '').replace(/\D/g, ''), 10) || 999;

/** La clé d'un item dans la page (menu, compteurs) : son test et son numéro. */
export const cleItem = (typeLabel, questionNum) => `${typeLabel}|${questionNum}`;

/**
 * Les questions de la banque rangées par test (type_label), dans l'ordre des évaluations, puis par item
 * (question_num) : l'item officiel, et les variantes déjà en base.
 */
export const ranger = (questions) => {
    const parTest = new Map();
    (questions || []).forEach((q) => {
        const typeLabel = q.type_label || 'Autres';
        const questionNum = q.question_num || '?';
        if (!parTest.has(typeLabel)) parTest.set(typeLabel, new Map());
        const items = parTest.get(typeLabel);
        if (!items.has(questionNum)) items.set(questionNum, { typeLabel, questionNum, officielle: null, variantes: [] });
        const item = items.get(questionNum);
        if (q.est_nationale && !item.officielle) item.officielle = q;
        else item.variantes.push(q);
    });
    return [...parTest.entries()]
        .map(([typeLabel, items]) => ({
            typeLabel,
            items: [...items.values()].sort((a, b) => numero(a.questionNum) - numero(b.questionNum)),
        }))
        .sort((a, b) => rangDuTest(a.typeLabel) - rangDuTest(b.typeLabel) || a.typeLabel.localeCompare(b.typeLabel));
};

const texteSansBalises = (html) => String(html || '').replace(/<[^>]+>/g, ' ');

/** Les items dont le numéro, l'intitulé ou l'énoncé contient tous les mots cherchés (accents et casse ignorés). */
export const filtrerItems = (groupes, recherche) => {
    const mots = normalize(recherche).split(/\s+/).filter(Boolean);
    if (mots.length === 0) return groupes;
    return groupes
        .map((groupe) => ({
            ...groupe,
            items: groupe.items.filter((item) => {
                const q = item.officielle || item.variantes[0] || {};
                const meule = normalize(`${item.questionNum} ${q.nom_label || ''} ${texteSansBalises(q.texte)} ${groupe.typeLabel}`);
                return mots.every((mot) => meule.includes(mot));
            }),
        }))
        .filter((groupe) => groupe.items.length > 0);
};

/** La clé d'un tirage : l'item officiel et le format (QCM et Saisie d'un même item sont deux cartes). */
export const cleTirage = (officielleId, format) => `evalnat_${officielleId}_${format}`;

/**
 * Un exercice tiré, prêt pour la sélection : un _tmpId unique (rang), l'item d'origine dans _meta.officielle.
 * Jamais de _meta.evalNatId : le lancement prendrait alors l'item officiel à la place de la variante.
 */
export const pourLaSelection = (payload, officielle, format, rang) => ({
    ...payload,
    _tmpId: `gen_${officielle.id}_${format}_${rang}`,
    _meta: {
        ...payload._meta,
        exerciseurKey: cleTirage(officielle.id, format),
        officielle: {
            id: officielle.id,
            labelId: officielle.label_id,
            questionNum: officielle.question_num,
            typeLabel: officielle.type_label,
        },
    },
});

/**
 * Un exercice ajouté depuis la carte d'un item (« + Ajouter »), prêt pour la sélection, ou la raison de son
 * refus. C'est déjà une variante de l'item : une variante de compréhension garde la référence de son texte
 * (`texte`, le SUPPORT du module ; la fenêtre « Modifier » de la carte ne la garde pas), et ce qu'une variante
 * ne sait pas porter (un choix en image, plusieurs bonnes réponses…) est refusé dès l'ajout plutôt qu'au
 * lancement. Rend { ok: true, element } ou { ok: false, erreur }.
 */
export const pourLAjout = (payload, officielle, { texte, format, rang }) => {
    const variante = texte && !referenceSupportLecture(payload) ? avecSupportLecture(payload, texte) : payload;
    try {
        versVarianteEvalNat(variante);
    } catch (e) {
        return { ok: false, erreur: `${officielle.question_num} : ${e.message}` };
    }
    return { ok: true, element: pourLaSelection(variante, officielle, format, rang) };
};

/**
 * Une variante en base telle que la fenêtre de QCMDashboard la reçoit pour être corrigée (format unifié).
 * Un texte à trous y retrouve son réglage strict, que la relecture commune ne rend pas : sans lui, la
 * correction rendrait la variante tolérante sans que le prof l'ait demandé.
 */
export const pourLaFenetre = (variante) => {
    const payload = toPayloadUnifie(variante);
    if (payload?.contenu?.subType !== 'texte_trou') return payload;
    const meta = (variante.reponses || []).find((r) => r?.type === 'trou_meta');
    return { ...payload, contenu: { ...payload.contenu, strict: Boolean(meta?.strict) } };
};

/**
 * Ce que la fenêtre rend pour une variante corrigée, prêt pour PATCH questions/<id>/, ou la raison du refus.
 * La variante garde la référence de son texte de compréhension, telle qu'elle était rangée (la fenêtre ne la
 * garde pas : sans elle, le livre du quiz rouvrirait le texte de septembre).
 * Rend { ok: true, champs } ou { ok: false, erreur }.
 */
export const pourLaCorrection = (donnees, variante) => {
    const reference = referenceSupportLecture(variante);
    const avecTexte = reference
        ? {
            ...donnees,
            elements_additionnels: { version: 1, elements: [], ...(donnees.elements_additionnels || {}), support_lecture: reference },
        }
        : donnees;
    try {
        return { ok: true, champs: versVarianteEvalNat(avecTexte) };
    } catch (e) {
        return { ok: false, erreur: e.message };
    }
};

/**
 * Lance le QCM de suivi. D'abord les variantes tirées, enregistrées une à une dans l'ordre de la fenêtre de
 * lancement ; ensuite seulement le QCM, avec les ids de tout ce qui a été choisi, dans cet ordre.
 * Un échec arrête tout : jamais de QCM à moitié. `deja` (Map _tmpId → id) garde les variantes déjà
 * enregistrées : un nouvel essai les reprend, sans doublon.
 * Rend { ok: true, resultat } ou { ok: false, erreur }.
 */
export const enregistrerPuisLancer = async (questions, { deja, enregistrerVariante, creerQcm }) => {
    for (const q of questions) {
        if (q._meta?.evalNatId || deja.has(q._tmpId)) continue;
        const item = q._meta?.officielle?.questionNum || 'Une variante';
        let id;
        try {
            id = await enregistrerVariante(q);
        } catch (e) {
            return { ok: false, erreur: `${item} : ${e.message}` };
        }
        if (!id) return { ok: false, erreur: `${item} : la variante n'a pas pu être enregistrée.` };
        deja.set(q._tmpId, id);
    }
    const ids = questions.map((q) => q._meta?.evalNatId ?? deja.get(q._tmpId));
    if (ids.length === 0 || ids.some((id) => !id)) {
        return { ok: false, erreur: 'Une question choisie n\'a pas pu être retrouvée : rien n\'a été créé.' };
    }
    const resultat = await creerQcm(ids);
    return resultat?.ok ? { ok: true, resultat } : { ok: false, erreur: resultat?.erreur || 'Le QCM n\'a pas pu être créé.' };
};
